import { getTicketStatuses } from 'api/ticket'
import ColorCode from 'components/colorCode'
import { LoadingContainer } from 'components/loadingContainer'
import { useAuditoriumSeats, useDrag, useZoom } from 'hooks/user/useAuditoriumSeats'
import { Tooltip } from 'primereact/tooltip'
import { useCallback, useEffect, useState } from 'react'
import { Ticket } from 'types/Event'
import { Seat, SeatStatus } from 'types/Location'
import { SeatStatusArray } from 'types/Ticket'

import {
    Cell,
    ContainerSmall,
    Grid,
    GridContainerSmall,
    GridWrapperSmall,
    TitleBox,
    WrapperColumn,
    WrapperColumnCode,
    WrapperRowCode,
    ZoomButton,
    ZoomContainer,
} from '../auditorium.style'
import { COLORS, SeatColors } from './constants'
import { SeatIcon } from './seatIcon'

interface Props {
    rows: number | null
    columns: number | null
    seats: Seat[]
    tickets: Ticket[]
    eventId: string
    middleColumn?: number
    currentSeatData: any
    success: boolean
    showColorCodes?: boolean
    isGallery?: boolean
    galleryName?: string
}

export const AuditoriumTicketScan = ({
    rows,
    columns,
    seats,
    tickets,
    eventId,
    middleColumn,
    currentSeatData,
    success,
    showColorCodes = false,
    isGallery = false,
    galleryName = 'Karzat',
}: Props) => {
    const { containerRef, handleDragStart, handleDragMove, handleDragEnd } = useDrag()
    const { zoomLevel, handleZoomIn, handleZoomOut } = useZoom(0, 0.6)

    const COLOR_CODES = [
        { color: '#000', label: 'Szabad ülőhelyek' },
        { color: '#F4C000', label: 'Eladott ülőhelyek' },
        { color: '#00AC11', label: 'Megérkezett' },
    ]

    const { generateSeatLabelData } = useAuditoriumSeats(seats, () => {}, tickets, eventId)

    const [ticketStatuses, setTicketStatuses] = useState<{ [key: string]: SeatStatus }>({})
    const [loadingTicketStatuses, setLoadingTicketStatuses] = useState<boolean>(false)

    const fetchTicketStatuses = useCallback(
        async (seatIds: string[]) => {
            setLoadingTicketStatuses(true)
            try {
                const statusesArray: SeatStatusArray = await getTicketStatuses(seatIds, eventId)
                const statusMap = Object.fromEntries(statusesArray.map(({ seat_id, status }) => [seat_id, status]))
                setTicketStatuses(statusMap)
            } catch (error) {
                console.error('Error fetching ticket statuses:', error)
            } finally {
                setLoadingTicketStatuses(false)
            }
        },
        [eventId],
    )

    useEffect(() => {
        if (seats && seats.length > 0) {
            const seatIds = seats.map((seat) => seat.seat_id!).filter(Boolean)
            fetchTicketStatuses(seatIds)
        }
    }, [seats, eventId, fetchTicketStatuses])

    const getSeatColor = (seat: Seat | undefined, seatStatus: SeatStatus): string => {
        if (!seat || !seat.seat_id) return SeatColors[SeatStatus.NotAvailable]

        const isLatestScanned = currentSeatData?.row === seat.row && currentSeatData?.column === seat.column

        if (isLatestScanned) {
            return success ? COLORS.currentlyScanned : COLORS.alreadyUsed
        }
        return SeatColors[seatStatus]
    }

    if (rows === null || columns === null) return null

    return (
        <LoadingContainer isLoading={loadingTicketStatuses} error={undefined}>
            <ContainerSmall>
                <WrapperColumn>
                    <TitleBox>Színpad</TitleBox>
                    <GridContainerSmall
                        ref={containerRef}
                        onMouseDown={handleDragStart}
                        onMouseMove={handleDragMove}
                        onMouseUp={handleDragEnd}
                        onMouseLeave={handleDragEnd}
                    >
                        <GridWrapperSmall
                            style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center', transition: 'transform 0.3s ease' }}
                        >
                            {Array.from({ length: rows }).map((val, rowIndex) => (
                                <Grid rows={1} columns={columns} key={rowIndex}>
                                    {Array.from({ length: columns }).map((value, columnIndex) => {
                                        const foundSeat = seats.find((s) => s.row === rowIndex && s.column === columnIndex)
                                        const seatStatus = foundSeat ? ticketStatuses[foundSeat.seat_id!] : SeatStatus.NotAvailable
                                        const fillColor = getSeatColor(foundSeat, seatStatus)
                                        const seatLabel = generateSeatLabelData(
                                            rowIndex,
                                            columnIndex,
                                            columns,
                                            seats,
                                            middleColumn,
                                            isGallery,
                                            galleryName,
                                        )?.label

                                        return (
                                            <Cell
                                                id={`seat-${rowIndex}-${columnIndex}-${foundSeat?.seat_id}`}
                                                key={foundSeat?.seat_id || `${rowIndex}-${columnIndex}`}
                                                onClick={true ? () => console.log(rowIndex, columnIndex) : undefined}
                                                style={{
                                                    cursor: true ? 'pointer' : 'not-allowed',
                                                }}
                                            >
                                                <Tooltip
                                                    target={`#seat-${rowIndex}-${columnIndex}-${foundSeat?.seat_id}`}
                                                    content={seatLabel}
                                                    position="top"
                                                    showOnDisabled
                                                    showDelay={400}
                                                    hideDelay={200}
                                                />
                                                <div>
                                                    <SeatIcon fill={fillColor} />
                                                </div>
                                            </Cell>
                                        )
                                    })}
                                </Grid>
                            ))}
                        </GridWrapperSmall>
                    </GridContainerSmall>
                    {showColorCodes && (
                        <WrapperRowCode>
                            <WrapperColumnCode hidden={!showColorCodes}>
                                {COLOR_CODES.map(({ color, label }) => (
                                    <ColorCode key={label} color={color} label={label} />
                                ))}
                            </WrapperColumnCode>
                            <ZoomContainer>
                                <ZoomButton type="button" onClick={handleZoomIn} icon="pi pi-search-plus" />
                                <ZoomButton type="button" onClick={handleZoomOut} icon="pi pi-search-minus" />
                            </ZoomContainer>
                        </WrapperRowCode>
                    )}
                    {!showColorCodes && (
                        <ZoomContainer style={{ alignSelf: 'flex-end' }}>
                            <ZoomButton type="button" onClick={handleZoomIn} icon="pi pi-search-plus" />
                            <ZoomButton type="button" onClick={handleZoomOut} icon="pi pi-search-minus" />
                        </ZoomContainer>
                    )}
                </WrapperColumn>
            </ContainerSmall>
        </LoadingContainer>
    )
}
